// Iter56 — Duplicate Plan Dialog
// Reusable modal launched from both the Plans list (row action) and the
// Plan editor toolbar. Lets the admin pick which sections to copy + the
// media handling strategy, then calls POST /admin/plans/{id}/duplicate
// and navigates to the new draft on success.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Copy, Loader2 } from "lucide-react";
import api from "@/lib/api";

// Section keys are the source of truth — must match the backend allowlist
// in server.py (`SECTION_MAP` inside admin_duplicate_plan).
export const DUPLICATE_SECTIONS = [
  { key: "basic",              label: "Basic Information",        hint: "Title mode, bedrooms, baths, sqft, stories, descriptions" },
  { key: "pricing",            label: "Pricing & Packages",       hint: "USD/INR prices, PDF/CAD/Revit tiers, deliverables" },
  { key: "categories",         label: "Categories",                hint: "Category IDs + primary category" },
  { key: "collections",        label: "Collections",               hint: "Collection assignments" },
  { key: "style",              label: "Style",                     hint: "Modern, French Provincial, etc." },
  { key: "features",           label: "Features",                  hint: "Selling-point bullets" },
  { key: "media",              label: "Images",                    hint: "Renderings & gallery images" },
  { key: "technical_drawings", label: "Technical Drawings",        hint: "Floor plans, elevations, sections" },
  { key: "videos",             label: "Videos",                    hint: "MP4 uploads + YouTube/Vimeo embeds" },
  { key: "pdf",                label: "PDFs",                      hint: "Marketing PDF (paid asset)" },
  { key: "ai",                 label: "AI Content",                hint: "Vision analysis output stored on the plan" },
  { key: "ai_approvals",       label: "AI Approvals",              hint: "Per-field publish flags · duplicate shows an inherited banner" },
  { key: "seo",                label: "SEO",                       hint: "Meta title, description, keywords, OG image" },
  { key: "homepage",           label: "Homepage Settings",         hint: "Featured flag & homepage priority" },
];

const DuplicatePlanDialog = ({ open, onOpenChange, plan, onDuplicated }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [sections, setSections] = useState(() =>
    Object.fromEntries(DUPLICATE_SECTIONS.map((s) => [s.key, true]))
  );
  const [mediaMode, setMediaMode] = useState("reference");

  React.useEffect(() => {
    if (open) {
      // Reset every time the dialog opens so a previous choice doesn't
      // silently apply to a different plan.
      setSections(Object.fromEntries(DUPLICATE_SECTIONS.map((s) => [s.key, true])));
      setMediaMode("reference");
    }
  }, [open]);

  const submit = async () => {
    if (!plan?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/plans/${plan.id}/duplicate`, {
        sections,
        media_mode: mediaMode,
      });
      toast.success(`Draft created: ${data.title}`, {
        description: `Project ${data.project_id} — opening the editor…`,
      });
      onDuplicated?.(data);
      onOpenChange(false);
      // Small delay so the toast is visible before the route change.
      setTimeout(() => navigate(`/admin/plans/${data.id}/edit`), 400);
    } catch (e) {
      toast.error("Duplicate failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const tickedCount = Object.values(sections).filter(Boolean).length;
  const setAll = (v) => setSections(Object.fromEntries(DUPLICATE_SECTIONS.map((s) => [s.key, v])));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="duplicate-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Duplicate plan</DialogTitle>
          <DialogDescription>
            Create a new <strong>Draft</strong> based on{" "}
            <span className="font-medium text-foreground">{plan?.title || "this plan"}</span>
            {plan?.project_id && <> · {plan.project_id}</>}. The duplicate receives a fresh Project ID, slug and Draft status.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Section allowlist */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-primary">What to copy · {tickedCount}/{DUPLICATE_SECTIONS.length} selected</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground"
                  onClick={() => setAll(false)}
                  data-testid="duplicate-select-none"
                >Select none</button>
                <button
                  type="button"
                  className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground"
                  onClick={() => setAll(true)}
                  data-testid="duplicate-select-all"
                >Select all</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 border border-border p-3 max-h-72 overflow-y-auto">
              {DUPLICATE_SECTIONS.map((s) => (
                <label key={s.key} className="flex items-start gap-3 py-1 cursor-pointer" data-testid={`duplicate-section-${s.key}`}>
                  <Checkbox
                    checked={sections[s.key]}
                    onCheckedChange={(c) => setSections((p) => ({ ...p, [s.key]: Boolean(c) }))}
                    data-testid={`duplicate-section-toggle-${s.key}`}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{s.label}</div>
                    <div className="text-[11px] text-muted-foreground leading-snug">{s.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Media mode */}
          <div>
            <div className="overline text-primary mb-2">Media handling</div>
            <div className="border border-border divide-y divide-border">
              <label className="flex items-start gap-3 p-3 cursor-pointer" data-testid="duplicate-media-reference">
                <input
                  type="radio"
                  name="media_mode"
                  value="reference"
                  checked={mediaMode === "reference"}
                  onChange={() => setMediaMode("reference")}
                  className="mt-1 accent-primary"
                />
                <div>
                  <div className="text-sm font-medium">Reference share <span className="text-[10px] uppercase tracking-[0.16em] text-primary ml-1">Recommended</span></div>
                  <div className="text-[11px] text-muted-foreground leading-snug">
                    Fast. The duplicate points at the same media files. Replacing an asset on the duplicate automatically switches only that file to an independent copy — everything else stays shared.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 cursor-pointer" data-testid="duplicate-media-deep-copy">
                <input
                  type="radio"
                  name="media_mode"
                  value="deep_copy"
                  checked={mediaMode === "deep_copy"}
                  onChange={() => setMediaMode("deep_copy")}
                  className="mt-1 accent-primary"
                />
                <div>
                  <div className="text-sm font-medium">Deep copy media</div>
                  <div className="text-[11px] text-muted-foreground leading-snug">
                    Create independent copies of every image, video, drawing and PDF. Use only when the source plan may later be deleted or the duplicate must be fully self-contained. Runs in the background and may take longer.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2 gap-2">
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} disabled={busy} data-testid="duplicate-cancel">
            Cancel
          </Button>
          <Button className="rounded-none bg-primary hover:bg-primary/90" onClick={submit} disabled={busy || tickedCount === 0} data-testid="duplicate-confirm">
            {busy ? <><Loader2 size={14} className="mr-2 animate-spin" />Creating draft…</> : <><Copy size={14} className="mr-2" />Create draft copy</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicatePlanDialog;
